import axios, { AxiosResponse } from 'axios';

import { Address } from 'common/address/Address';
import { SearchRequest } from 'core/util/Search';

export enum ProfileState {
    Active = 0,
    Closed = 1,
    Cancelled = 2,
    Moved = 3,
}

export enum ProfileType {
    RoofTop = 0,
    Person = 1,
    ServiceArea = 2,
    Online = 3,
}

export enum WorkflowState {
    Draft = 0,
    SubmittedForApproval = 1,
    Published = 2,
    History = 3,
    Unmanaged = 4,
    SubmittedForCleansing = 5,
}

export interface LocationParameter {
    name: string;
    value: string;
    userDefined: boolean;
}

export interface LocationAttribute {
    name: string;
    value: string;
}

export enum Field {
    ExternalName = '__profile_external_name__',
    Places = '__profile_places__',
    ProfileTenantId = '__profile_tenant_id__',
    LocationUid = '__profile_location_uid__',
    LocationCode = '__profile_location_code__',
    ProfileType = '__profile_type__',
    StreetAddress1 = '__profile_address_street_address1__',
    WorkflowState = '__profile_workflow_state__',
    Website = '__profile_website__',
    PersonaId = '__persona_id__',
    AddressCountry = '__profile_address_country__',
}

export interface Place {
    placeID: string;
    officeName: string;
    address: Address;
}
/**
  4: ServiceArea serviceArea
  5: list<BusinessHour> businessHours
  6: list<Phone> phones
  7: list<Website> websites
  8: list<Email> emails
  9: bool primary,
  10: OfficeStatus officeStatus,
  11: R4EAddress.Address originalAddress,
  12: optional R4ECommon.LocationUid linkedLuid
 */

export interface Profile {
    id: string;
    agencyID: number;
    auditData?: any;
    tenantID: number;
    locationUid: number;
    fields?: { [key: string]: any };
    personaID: string;
    parentID?: string;
    optedOut: boolean;
    industryID: string;
    state: ProfileState;
    type: ProfileType;
    workflowState: WorkflowState;
    distance?: number;
    slug?: string;
    publishable?: { [key: string]: boolean };
    optOutSourceIDs?: string[];
    externalID: string;
    internalName: string;
    pdName: string;
    missingFields: string[];
    locationParameters: LocationParameter[];
    linkedProfileID: string;
    primaryProfile: boolean;
    locationAttributes: LocationAttribute[];
    dateCreated: string;
    dateUpdated: string;
    profileLocatorName: string;
    locationCode?: string;
}

export interface SaveProfileOptions {
    skipAddressNormalization?: boolean;
    skipLatLongValidation?: boolean;
    validateListings?: boolean;
    optimizeAttributes?: boolean;
    validateSources?: string[];
}

export const submitProfileChanges = async (
    agencyId: number,
    tenantId: number,
    profile: Profile,
    args: SaveProfileOptions,
): Promise<Profile> => {
    const params: any = { agencyId, tenantId, profile, args };

    const response: AxiosResponse = await axios.post(`/api/tenants/${tenantId}/profiles/submit-changes`, params);

    return response.data;
};

export const getProfiles = async (tenantId: number, search: SearchRequest, args?: any): Promise<Profile[]> => {
    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/profiles`, { params: { search, args } });

    return response.data;
};

export const searchProfilesElastic = async (
    agencyId: number,
    tenantId: number,
    search: SearchRequest,
    args: { useElastic?: boolean; [key: string]: any } = { useElastic: false },
    requestSource?: any,
): Promise<{ profiles: Profile[]; count: number }> => {
    const { data } = await axios.post(
        `/api/agencies/${agencyId}/tenants/${tenantId}/profiles/search-elastic`,
        {
            agencyId,
            tenantId,
            search,
            args,
        },
        { cancelToken: requestSource?.token },
    );

    return data;
};

export const facetedSearchProfilesElastic = async (
    agencyId: number,
    tenantId: number,
    args: any,
    requestSource?: any,
): Promise<{ json: any[] }> => {
    const { data } = await axios.post(
        `/api/tenants/${tenantId}/profiles-faceted-elastic-search`,
        {
            agencyId,
            tenantId,
            args,
        },
        {
            cancelToken: requestSource?.token,
        },
    );

    return data;
};

export const countProfiles = async (
    tenantId: number,
    search: SearchRequest,
    args = { useElastic: false },
): Promise<{ count: number }> => {
    const { data } = await axios.get(
        `/api/tenants/${tenantId}/profiles-count?search=${JSON.stringify(search)}&args=${JSON.stringify(args)}`,
    );

    return data;
};
